/**
 * "Buscar canción para añadir…" of Figma CMS-06 / CMS-08: a debounced server-side search that
 * proposes candidates and calls `onPick`. Every editorial picker (songs, playlists, heroes)
 * passes its own `search` function, so this component knows nothing about the API surface.
 */
import { useQuery } from '@tanstack/react-query';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { useT } from '../../i18n';
import { cn } from '../../lib/cn';
import { Input } from '../ui/field';
import { swatch } from './OrderedList';

export interface SearchCandidate {
  id: string;
  title: string;
  subtitle?: string;
  imageUrl?: string | null;
  tag?: string;
}

export interface EntitySearchProps {
  /** Query key segment identifying what is being searched (`songs`, `playlists`, `heroes`). */
  kind: string;
  placeholder: string;
  search: (term: string) => Promise<SearchCandidate[]>;
  onPick: (candidate: SearchCandidate) => void;
  /** Already-picked ids, filtered out of the results. */
  exclude: readonly string[];
  disabled?: boolean;
}

/** Debounce of the apps' search (docs/03 §2) — the console is not more urgent. */
export const SEARCH_DEBOUNCE_MS = 200;

export function EntitySearch({
  kind,
  placeholder,
  search,
  onPick,
  exclude,
  disabled = false,
}: EntitySearchProps) {
  const { t } = useT();
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();
  const optionId = (index: number) => `${listId}-option-${index}`;

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(term.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [term]);

  const results = useQuery({
    queryKey: ['cms', 'picker', kind, debounced],
    queryFn: () => search(debounced),
    enabled: debounced.length > 0 && !disabled,
  });

  const candidates = (results.data ?? []).filter((item) => !exclude.includes(item.id));
  const open = debounced.length > 0;
  const highlighted = candidates.length === 0 ? -1 : Math.min(active, candidates.length - 1);

  /**
   * QA 2026-09-15 **UX-538**: picking a result unmounted the focused button under the operator's
   * own focus, so `document.activeElement` became `<body>` — outside the drawer that claims
   * `aria-modal` — and the next Tab restarted at the skip link. Focus goes back to the field the
   * operator was typing in, which is also where they want to type the next title.
   */
  const pick = (candidate: SearchCandidate) => {
    onPick(candidate);
    setTerm('');
    setDebounced('');
    setActive(0);
    input.current?.focus();
  };

  /** The combobox reflex (type → ArrowDown → Enter) used to pick nothing: a plain button list. */
  const onKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (!open || candidates.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((index) => (index + 1) % candidates.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => (index - 1 + candidates.length) % candidates.length);
    } else if (event.key === 'Enter' && highlighted >= 0) {
      event.preventDefault();
      pick(candidates[highlighted]!);
    } else if (event.key === 'Escape') {
      setTerm('');
      setDebounced('');
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <Input
        ref={input}
        placeholder={placeholder}
        aria-label={placeholder}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && highlighted >= 0 ? optionId(highlighted) : undefined}
        value={term}
        disabled={disabled}
        onChange={(event) => {
          setTerm(event.target.value);
          setActive(0);
        }}
        onKeyDown={onKeyDown}
        data-testid={`search-${kind}`}
      />
      {open && (
        <div
          id={listId}
          role="listbox"
          aria-label={placeholder}
          className="max-h-56 overflow-auto rounded-lg border border-line bg-panel py-1"
        >
          {results.isPending && (
            <p className="px-3 py-2 text-[12px] text-muted">{t('common:states.loading')}</p>
          )}
          {!results.isPending && candidates.length === 0 && (
            <p className="px-3 py-2 text-[12px] text-muted">{t('cms:editorial.noResults')}</p>
          )}
          {candidates.map((candidate, index) => (
            <button
              key={candidate.id}
              id={optionId(index)}
              type="button"
              role="option"
              aria-selected={index === highlighted}
              className={cn(
                'flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-canvas',
                index === highlighted && 'bg-canvas',
              )}
              onClick={() => pick(candidate)}
            >
              {candidate.imageUrl ? (
                <img src={candidate.imageUrl} alt="" className="size-7 rounded-md object-cover" />
              ) : (
                <span
                  aria-hidden="true"
                  className="size-7 rounded-md"
                  style={{ background: swatch(candidate.id) }}
                />
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold text-ink">
                  {candidate.title}
                </span>
                {candidate.subtitle && (
                  <span className="block truncate text-[12px] text-muted">
                    {candidate.subtitle}
                  </span>
                )}
              </span>
              {candidate.tag && <span className="text-[12px] text-muted">{candidate.tag}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
